import { useCallback, useEffect, useRef, useState } from 'react';
import { getEventBus } from '@alfa-bank/newclick-core/window-utils';
import type { OpenSidePanelsParams } from '@alfa-bank/newclick-types/open-side-panel-params';

import { useSidePanelsStack } from './use-side-panels-stack';

export const useSidePanels = () => {
    const [open, setOpen] = useState(false);
    const [sidePanelParams, setSidePanelParams] = useState<OpenSidePanelsParams>();

    const refCurrentSidePanelId = useRef(sidePanelParams?.sidePanelId);

    const { getPreviousSidePanel } = useSidePanelsStack({
        open,
        sidePanelParams,
    });

    const emitChangeSidePanelEvent = (
        state: 'open' | 'close',
        sidePanelId?: OpenSidePanelsParams['sidePanelId'],
    ) => {
        const eventBus = getEventBus();

        if (sidePanelId) {
            refCurrentSidePanelId.current = sidePanelId;
        }

        if (refCurrentSidePanelId.current) {
            eventBus?.emit('CHANGE_SIDE_PANEL_STATE', {
                sidePanelId: refCurrentSidePanelId.current,
                state,
            });
        }
    };

    useEffect(() => {
        window.openSidePanel = (params: OpenSidePanelsParams) => {
            setSidePanelParams(params);
            setOpen(true);
            emitChangeSidePanelEvent('open', params.sidePanelId);
        };

        window.closeSidePanel = () => {
            setOpen(false);
            emitChangeSidePanelEvent('close');
        };
    }, []);

    const openPreviousSidePanel = useCallback(() => {
        const previousSidePanelParams = getPreviousSidePanel();

        if (previousSidePanelParams) {
            setSidePanelParams(previousSidePanelParams);
            emitChangeSidePanelEvent('open', previousSidePanelParams.sidePanelId);
        }
    }, [getPreviousSidePanel]);

    useEffect(() => {
        window.openPreviousSidePanel = openPreviousSidePanel;
        window.isSidePanelOpen = open;
    }, [open, openPreviousSidePanel]);

    return {
        isSidePanelOpen: open,
        sidePanelParams,
        openPreviousSidePanel,
        setSidePanelParams,
    };
};
